import { useUser } from "@clerk/expo";
import * as ImagePicker from "expo-image-picker";
import { ImagePlus } from "lucide-react-native";
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DimensionValue, Platform } from "react-native";

import { useLocalization } from "@/hooks";
import { Box, HStack, Pressable, Text, VStack } from "../base";
import Media from "../Media";
import MediaGallery from "../MediaGallery";

/* ── Types ──────────────────────────────────────────────── */

type MediaType = "images" | "videos" | "all";

type MediaItem = ImagePicker.ImagePickerAsset | string;

type DragHandlers = {
  onDragEnter: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => Promise<void>;
};

export interface MediaPickerController {
  canAddMore: boolean;
  effectiveMaxFileSize: number;
  error: string | null;
  hint?: string;
  isAdmin: boolean;
  isDisabled: boolean;
  isDragging: boolean;
  maxItems: number;
  media: MediaItem[];
  mediaType: MediaType;
  openGallery: (index: number) => void;
  pickMedia: () => Promise<void>;
  removeMedia: (index: number) => void;
  /** Spread onto any element to make it a drop zone (web only) */
  webDragHandlers: DragHandlers;
}

interface MediaPickerProps {
  children?: ReactNode;
  compact?: boolean;
  height?: number;
  hint?: string;
  icon?: ReactNode;
  /**
   * Pixel size of the placeholder upload icon (the `ImagePlus` glyph) in the
   * empty trigger. The colored bubble around it scales proportionally. Use
   * this to keep the trigger readable when shrinking `compact` pickers below
   * the default 96×96 square (e.g. 40×40 inline thumbnails want `iconSize:
   * 12`). Defaults: 24 in the full-sized trigger, 16 in compact mode.
   */
  iconSize?: number;
  isDisabled?: boolean;
  maxFileSize?: number; // MB. Defaults: 5 for images, 500 for videos.
  maxItems?: number;
  media: MediaItem[];
  mediaType?: MediaType;
  onMediaChange: (media: MediaItem[]) => void;
  width?: DimensionValue;
}

/* ── Constants ──────────────────────────────────────────── */

const MB_TO_BYTES = 1024 * 1024;
const DEFAULT_IMAGE_MAX_SIZE = 5;
const DEFAULT_VIDEO_MAX_SIZE = 500;

const fitsSize = (size: number, maxMB: number) => size < maxMB * MB_TO_BYTES;

/* ── Context ────────────────────────────────────────────── */

const MediaPickerContext = createContext<MediaPickerController | null>(null);

export const useMediaPicker = (): MediaPickerController => {
  const ctx = useContext(MediaPickerContext);
  if (!ctx) {
    throw new Error(
      "useMediaPicker must be used inside a <MediaPicker> component"
    );
  }
  return ctx;
};

/* ── Internal hook: owns all state + returns host-only bits + controller ── */

interface InternalState {
  controller: MediaPickerController;
  galleryIndex: number;
  galleryVisible: boolean;
  mediaUris: string[];
  setGalleryVisible: (visible: boolean) => void;
}

const useMediaPickerInternals = (
  options: Omit<
    MediaPickerProps,
    "children" | "compact" | "height" | "icon" | "width"
  >
): InternalState => {
  const {
    hint,
    isDisabled = false,
    maxFileSize,
    maxItems = 5,
    media,
    mediaType = "all",
    onMediaChange,
  } = options;

  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.admin === true;
  const { i18n } = useLocalization();

  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryVisible, setGalleryVisible] = useState(false);

  // Keep the latest `media` available to deferred callbacks (asset processing,
  // gallery actions) without recreating them. Sync in an effect so we don't
  // write the ref during render.
  const mediaRef = useRef(media);
  useEffect(() => {
    mediaRef.current = media;
  });

  const effectiveMaxFileSize =
    maxFileSize ??
    (mediaType === "videos" ? DEFAULT_VIDEO_MAX_SIZE : DEFAULT_IMAGE_MAX_SIZE);

  const processAssets = useCallback(
    (
      assets: ImagePicker.ImagePickerAsset[],
      currentMedia: ImagePicker.ImagePickerAsset[]
    ) => {
      const newMedia = [...currentMedia];
      let rejectedCount = 0;
      let rejectedReason = "";

      for (const asset of assets) {
        const isVideo = asset.type === "video";
        const assetMaxSize = isVideo
          ? Math.max(effectiveMaxFileSize, DEFAULT_VIDEO_MAX_SIZE)
          : effectiveMaxFileSize;

        if (asset.fileSize && !fitsSize(asset.fileSize, assetMaxSize)) {
          rejectedCount++;
          rejectedReason = isVideo
            ? i18n.t("misc.fileSizeExceededVideo", { maxSize: assetMaxSize })
            : i18n.t("misc.fileSizeExceededImage", { maxSize: assetMaxSize });
          continue;
        }

        if (isAdmin || newMedia.length < maxItems) {
          newMedia.push(asset);
        } else {
          rejectedCount++;
          rejectedReason = i18n.t("misc.maxItemsReached", { max: maxItems });
        }
      }

      return { newMedia, rejectedCount, rejectedReason };
    },
    [effectiveMaxFileSize, i18n, isAdmin, maxItems]
  );

  const pickMedia = useCallback(async () => {
    setError(null);
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      setError(i18n.t("misc.permissionRequiredMessage"));
      return;
    }

    let pickerMediaTypes: ImagePicker.MediaType[];
    if (mediaType === "images") pickerMediaTypes = ["images"];
    else if (mediaType === "videos") pickerMediaTypes = ["videos"];
    else pickerMediaTypes = ["images", "videos"];

    const result = await ImagePicker.launchImageLibraryAsync({
      allowsMultipleSelection: true,
      mediaTypes: pickerMediaTypes,
      quality: 0.8,
      videoMaxDuration: 60,
    });

    if (!result.canceled && result.assets) {
      const { newMedia, rejectedCount, rejectedReason } = processAssets(
        result.assets,
        mediaRef.current as ImagePicker.ImagePickerAsset[]
      );

      if (rejectedCount > 0) {
        setError(
          rejectedCount > 1
            ? i18n.t("misc.filesRejected", {
                count: rejectedCount,
                reason: rejectedReason,
              })
            : rejectedReason
        );
      } else {
        setError(null);
      }

      onMediaChange(newMedia);
    }
  }, [i18n, mediaType, onMediaChange, processAssets]);

  const removeMedia = useCallback(
    (index: number) => {
      setError(null);
      const newMedia = [...media];
      newMedia.splice(index, 1);
      onMediaChange(newMedia);
    },
    [media, onMediaChange]
  );

  const openGallery = useCallback((index: number) => {
    setGalleryIndex(index);
    setGalleryVisible(true);
  }, []);

  const getFileDimensions = useCallback(
    (
      url: string,
      isVideo: boolean
    ): Promise<{ height: number; width: number }> =>
      new Promise((resolve) => {
        if (isVideo) {
          const video = document.createElement("video");
          video.preload = "metadata";
          video.onloadedmetadata = () =>
            resolve({
              height: video.videoHeight,
              width: video.videoWidth,
            });
          video.onerror = () => resolve({ height: 0, width: 0 });
          video.src = url;
        } else {
          const img = new Image();
          img.onload = () => resolve({ height: img.height, width: img.width });
          img.onerror = () => resolve({ height: 0, width: 0 });
          img.src = url;
        }
      }),
    []
  );

  const webDragHandlers = useMemo<DragHandlers>(
    () => ({
      onDragEnter: (e) => {
        if (Platform.OS !== "web" || isDisabled) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
      },
      onDragOver: (e) => {
        if (Platform.OS !== "web" || isDisabled) return;
        e.preventDefault();
        e.stopPropagation();
      },
      onDragLeave: (e) => {
        if (Platform.OS !== "web" || isDisabled) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
      },
      onDrop: async (e) => {
        if (Platform.OS !== "web" || isDisabled) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setError(null);

        const files = Array.from(e.dataTransfer.files);
        if (files.length === 0) return;

        const allowedTypes: string[] = [];
        if (mediaType === "images" || mediaType === "all") {
          allowedTypes.push(
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/gif",
            "image/webp"
          );
        }
        if (mediaType === "videos" || mediaType === "all") {
          allowedTypes.push(
            "video/mp4",
            "video/quicktime",
            "video/x-m4v",
            "video/webm"
          );
        }

        const validFiles = files.filter((f) => allowedTypes.includes(f.type));

        if (validFiles.length === 0) {
          setError(
            mediaType === "videos"
              ? i18n.t("misc.invalidVideoFormat")
              : mediaType === "images"
                ? i18n.t("misc.invalidImageFormat")
                : i18n.t("misc.invalidMediaFormat")
          );
          return;
        }

        const assets: ImagePicker.ImagePickerAsset[] = await Promise.all(
          validFiles.map(async (file) => {
            const uri = URL.createObjectURL(file);
            const isVideo = file.type.startsWith("video/");
            const dimensions = await getFileDimensions(uri, isVideo);

            return {
              assetId: null,
              base64: null,
              duration: null,
              exif: null,
              fileName: file.name,
              fileSize: file.size,
              height: dimensions.height,
              mimeType: file.type,
              type: isVideo ? "video" : "image",
              uri,
              width: dimensions.width,
            } as ImagePicker.ImagePickerAsset;
          })
        );

        const { newMedia, rejectedCount, rejectedReason } = processAssets(
          assets,
          mediaRef.current as ImagePicker.ImagePickerAsset[]
        );

        if (rejectedCount > 0) {
          setError(
            rejectedCount > 1
              ? i18n.t("misc.filesRejected", {
                  count: rejectedCount,
                  reason: rejectedReason,
                })
              : rejectedReason
          );
        } else {
          setError(null);
        }

        onMediaChange(newMedia);
      },
    }),
    [
      getFileDimensions,
      i18n,
      isDisabled,
      mediaType,
      onMediaChange,
      processAssets,
    ]
  );

  const canAddMore = isAdmin || media.length < maxItems;

  const mediaUris = useMemo(
    () => media.map((item) => (typeof item === "string" ? item : item.uri)),
    [media]
  );

  const controller = useMemo<MediaPickerController>(
    () => ({
      canAddMore,
      effectiveMaxFileSize,
      error,
      hint,
      isAdmin,
      isDisabled,
      isDragging,
      maxItems,
      media,
      mediaType,
      openGallery,
      pickMedia,
      removeMedia,
      webDragHandlers,
    }),
    [
      canAddMore,
      effectiveMaxFileSize,
      error,
      hint,
      isAdmin,
      isDisabled,
      isDragging,
      maxItems,
      media,
      mediaType,
      openGallery,
      pickMedia,
      removeMedia,
      webDragHandlers,
    ]
  );

  return {
    controller,
    galleryIndex,
    galleryVisible,
    mediaUris,
    setGalleryVisible,
  };
};

/* ── Sub-components (consume context) ───────────────────── */

interface MediaPickerTriggerProps {
  children?: ReactNode;
  compact?: boolean;
  height?: number;
  iconSize?: number;
  width?: DimensionValue;
}

const MediaPickerTrigger: FC<MediaPickerTriggerProps> = ({
  children,
  compact = false,
  height,
  iconSize,
  width,
}) => {
  const { i18n } = useLocalization();
  const {
    canAddMore,
    effectiveMaxFileSize,
    isDisabled,
    isDragging,
    mediaType,
    pickMedia,
    webDragHandlers,
  } = useMediaPicker();

  // Mirror the legacy default layout: once max items is reached, the trigger
  // disappears so the user can't pick more (errors are still surfaced via
  // <MediaPicker.Error /> if a pick is somehow attempted).
  if (!canAddMore) return null;

  // Custom icon variant: consumer supplies any element as the trigger
  if (children) {
    return (
      <Pressable
        className={isDisabled ? "opacity-50" : ""}
        disabled={isDisabled}
        onPress={pickMedia}
      >
        {children}
      </Pressable>
    );
  }

  const getSelectLabel = () => {
    if (mediaType === "videos") return i18n.t("misc.tapToSelectVideos");
    if (mediaType === "images") return i18n.t("misc.tapToSelectImages");
    return i18n.t("misc.tapToSelectMedia");
  };

  if (compact) {
    const size = {
      height: height ?? 96,
      width: (width ?? 96) as DimensionValue,
    };
    const resolvedIconSize = iconSize ?? 16;
    const bubblePadding = Math.max(2, Math.round(resolvedIconSize / 4));
    return (
      <Box
        {...(Platform.OS === "web"
          ? (webDragHandlers as unknown as DragHandlers)
          : {})}
      >
        <Pressable
          className={`
            border-2 border-dashed rounded-lg items-center justify-center border-outline-300 bg-background-50
            ${isDisabled ? "opacity-50" : isDragging ? "border-primary-500 bg-primary-50" : "hover:border-primary-400 hover:bg-primary-25"}
          `}
          disabled={isDisabled}
          onPress={pickMedia}
          style={size}
        >
          <Box
            className="rounded-full bg-outline-200"
            style={{ padding: bubblePadding }}
          >
            <ImagePlus
              color="rgb(var(--color-typography-500))"
              size={resolvedIconSize}
            />
          </Box>
        </Pressable>
      </Box>
    );
  }

  return (
    <Box
      {...(Platform.OS === "web"
        ? (webDragHandlers as unknown as DragHandlers)
        : {})}
    >
      <Pressable
        className={`
          border-2 border-dashed rounded-xl p-6 border-outline-300 bg-background-50
          ${!width ? "w-full" : ""} ${!height ? "min-h-32" : ""}
          ${isDisabled ? "opacity-50" : isDragging ? "border-primary-500 bg-primary-50" : "hover:border-primary-400 hover:bg-primary-25"}
        `}
        disabled={isDisabled}
        onPress={pickMedia}
        style={width || height ? { width, height } : undefined}
      >
        <VStack className="items-center justify-center space-y-3">
          <Box className="p-3 rounded-full bg-outline-200">
            <ImagePlus color="rgb(var(--color-typography-500))" size={24} />
          </Box>
          <VStack className="items-center space-y-1">
            <Text className="text-sm font-medium text-typography-700">
              {isDragging ? i18n.t("misc.dropFilesHere") : getSelectLabel()}
            </Text>
            <Text className="text-xs text-typography-500">
              {mediaType === "videos"
                ? i18n.t("misc.videoUpToSize", {
                    maxSize: effectiveMaxFileSize,
                  })
                : mediaType === "images"
                  ? i18n.t("misc.imageUpToSize", {
                      maxSize: effectiveMaxFileSize,
                    })
                  : i18n.t("misc.mediaUpToSize", {
                      imageMaxSize: effectiveMaxFileSize,
                      videoMaxSize: effectiveMaxFileSize,
                    })}
            </Text>
          </VStack>
        </VStack>
      </Pressable>
    </Box>
  );
};

interface MediaPickerThumbnailsProps {
  /** Space between thumbnails in px. */
  gap?: number;
  /** Thumbnail height in px. Width adapts to the real media aspect ratio. */
  height?: number;
}

const MediaPickerThumbnails: FC<MediaPickerThumbnailsProps> = ({
  gap = 12,
  height = 96,
}) => {
  const { i18n } = useLocalization();
  const { isDisabled, media, openGallery, removeMedia } = useMediaPicker();

  if (media.length === 0) return null;

  return (
    <HStack className="flex-wrap" style={{ gap }}>
      {media.map((item, index) => (
        <Media
          key={index}
          alt={i18n.t("misc.selectedImageAlt", { index: index + 1 })}
          height={height}
          onDelete={!isDisabled ? () => removeMedia(index) : undefined}
          onPress={() => openGallery(index)}
          source={item}
        />
      ))}
    </HStack>
  );
};

const MediaPickerError: FC = () => {
  const { error } = useMediaPicker();
  if (!error) return null;
  return <Text className="text-sm text-error-500">{error}</Text>;
};

/* ── Main MediaPicker (default layout + compound shell) ── */

const MediaPicker: FC<MediaPickerProps> & {
  Error: typeof MediaPickerError;
  Thumbnails: typeof MediaPickerThumbnails;
  Trigger: typeof MediaPickerTrigger;
} = ({
  children,
  compact = false,
  height,
  hint,
  icon,
  iconSize,
  isDisabled = false,
  maxFileSize,
  maxItems = 5,
  media,
  mediaType = "all",
  onMediaChange,
  width,
}) => {
  const { i18n } = useLocalization();
  const {
    controller,
    galleryIndex,
    galleryVisible,
    mediaUris,
    setGalleryVisible,
  } = useMediaPickerInternals({
    hint,
    isDisabled,
    maxFileSize,
    maxItems,
    media,
    mediaType,
    onMediaChange,
  });

  const gallery = (
    <MediaGallery
      initialIndex={galleryIndex}
      media={mediaUris}
      onClose={() => setGalleryVisible(false)}
      visible={galleryVisible}
    />
  );

  /* Compound mode: children control the layout */
  if (children !== undefined) {
    return (
      <MediaPickerContext.Provider value={controller}>
        {children}
        {gallery}
      </MediaPickerContext.Provider>
    );
  }

  /* Legacy default layout, unchanged behaviour */
  const hasMedia = media.length > 0;
  const {
    canAddMore,
    effectiveMaxFileSize,
    error,
    isAdmin,
    isDragging,
    openGallery,
    pickMedia,
    removeMedia,
    webDragHandlers,
  } = controller;

  const getSelectLabel = () => {
    if (mediaType === "videos") return i18n.t("misc.tapToSelectVideos");
    if (mediaType === "images") return i18n.t("misc.tapToSelectImages");
    return i18n.t("misc.tapToSelectMedia");
  };

  const getUploadLabel = () => {
    if (isAdmin) return getSelectLabel();
    if (mediaType === "videos")
      return i18n.t("misc.uploadVideos", { max: maxItems });
    if (mediaType === "images")
      return i18n.t("misc.uploadImages", { max: maxItems });
    return i18n.t("misc.uploadMedia", { max: maxItems });
  };

  if (compact) {
    const compactMediaSize = {
      height: height ?? 80,
      width: (width ?? 80) as DimensionValue,
    };
    const compactPickerSize = {
      height: height ?? 96,
      width: (width ?? 96) as DimensionValue,
    };
    const sizeStyle =
      width || height ? { width, height: compactPickerSize.height } : undefined;

    return (
      <MediaPickerContext.Provider value={controller}>
        <VStack className="gap-2" style={sizeStyle}>
          {hasMedia ? (
            <Media
              alt={i18n.t("misc.selectedImageAlt", { index: 1 })}
              height={compactMediaSize.height}
              onDelete={!isDisabled ? () => removeMedia(0) : undefined}
              onPress={() => openGallery(0)}
              resizeMode="cover"
              source={media[0]}
              width={compactMediaSize.width}
            />
          ) : (
            <Box
              style={sizeStyle}
              {...(Platform.OS === "web"
                ? (webDragHandlers as unknown as DragHandlers)
                : {})}
            >
              <Pressable
                className={`
                  border-2 border-dashed rounded-lg items-center justify-center border-outline-300 bg-background-50
                  ${isDisabled ? "opacity-50" : isDragging ? "border-primary-500 bg-primary-50" : "hover:border-primary-400 hover:bg-primary-25"}
                `}
                disabled={isDisabled}
                onPress={pickMedia}
                style={compactPickerSize}
              >
                <Box
                  className="rounded-full bg-outline-200"
                  style={{
                    padding: Math.max(2, Math.round((iconSize ?? 16) / 4)),
                  }}
                >
                  <ImagePlus
                    color="rgb(var(--color-typography-500))"
                    size={iconSize ?? 16}
                  />
                </Box>
              </Pressable>
            </Box>
          )}
          {error && <Text className="text-xs text-error-500">{error}</Text>}
          {gallery}
        </VStack>
      </MediaPickerContext.Provider>
    );
  }

  return (
    <MediaPickerContext.Provider value={controller}>
      <VStack className="w-full space-y-3">
        {!icon && (
          <VStack className="gap-1">
            <Text className="text-sm font-semibold text-typography-800">
              {getUploadLabel()}
            </Text>
            {hint && (
              <Text className="text-xs text-typography-400">{hint}</Text>
            )}
          </VStack>
        )}

        {!hasMedia && !icon ? (
          <Box
            {...(Platform.OS === "web"
              ? (webDragHandlers as unknown as DragHandlers)
              : {})}
          >
            <Pressable
              className={`
                border-2 border-dashed rounded-xl p-6 border-outline-300 bg-background-50
                ${!width ? "w-full" : ""} ${!height ? "min-h-32" : ""}
                ${isDisabled ? "opacity-50" : isDragging ? "border-primary-500 bg-primary-50" : "hover:border-primary-400 hover:bg-primary-25"}
              `}
              disabled={isDisabled}
              onPress={pickMedia}
              style={width || height ? { width, height } : undefined}
            >
              <VStack className="items-center justify-center space-y-3">
                <Box className="p-3 rounded-full bg-outline-200">
                  <ImagePlus
                    color="rgb(var(--color-typography-500))"
                    size={24}
                  />
                </Box>
                <VStack className="items-center space-y-1">
                  <Text className="text-sm font-medium text-typography-700">
                    {isDragging
                      ? i18n.t("misc.dropFilesHere")
                      : getSelectLabel()}
                  </Text>
                  <Text className="text-xs text-typography-500">
                    {mediaType === "videos"
                      ? i18n.t("misc.videoUpToSize", {
                          maxSize: effectiveMaxFileSize,
                        })
                      : mediaType === "images"
                        ? i18n.t("misc.imageUpToSize", {
                            maxSize: effectiveMaxFileSize,
                          })
                        : i18n.t("misc.mediaUpToSize", {
                            imageMaxSize: maxFileSize ?? DEFAULT_IMAGE_MAX_SIZE,
                            videoMaxSize: maxFileSize ?? DEFAULT_VIDEO_MAX_SIZE,
                          })}
                  </Text>
                </VStack>
              </VStack>
            </Pressable>
          </Box>
        ) : (
          <HStack className={`flex-wrap gap-3 ${icon ? "items-end" : ""}`}>
            {canAddMore && (
              <>
                {icon ? (
                  <Pressable
                    className={isDisabled ? "opacity-50" : ""}
                    disabled={isDisabled}
                    onPress={pickMedia}
                  >
                    {icon}
                  </Pressable>
                ) : (
                  <Box
                    {...(Platform.OS === "web"
                      ? (webDragHandlers as unknown as DragHandlers)
                      : {})}
                  >
                    <Pressable
                      className={`border-2 border-dashed rounded-lg items-center justify-center border-outline-300 bg-background-50 ${isDragging ? "border-primary-500 bg-primary-50" : "hover:border-primary-400 hover:bg-primary-25"}`}
                      disabled={isDisabled}
                      onPress={pickMedia}
                      style={{ height: icon ? 64 : 96, width: icon ? 64 : 96 }}
                    >
                      <Box className="p-2 rounded-full bg-outline-200">
                        <ImagePlus
                          color="rgb(var(--color-typography-500))"
                          size={16}
                        />
                      </Box>
                    </Pressable>
                  </Box>
                )}
              </>
            )}
            {media.map((item, index) => (
              <Media
                key={index}
                alt={i18n.t("misc.selectedImageAlt", { index: index + 1 })}
                height={icon ? 64 : 96}
                onDelete={!isDisabled ? () => removeMedia(index) : undefined}
                onPress={() => openGallery(index)}
                source={item}
              />
            ))}
          </HStack>
        )}

        {error && <Text className="text-sm text-error-500">{error}</Text>}
        {gallery}
      </VStack>
    </MediaPickerContext.Provider>
  );
};

MediaPicker.Error = MediaPickerError;
MediaPicker.Thumbnails = MediaPickerThumbnails;
MediaPicker.Trigger = MediaPickerTrigger;

export default MediaPicker;
